/** Line-of-business carried through Form Builder forms — drives Parrot's
 *  consumer-side AU sections and the carrier list. Matches the Phoenix
 *  `lead_forms.insurance_line` column. */
export type InsuranceLine = 'personal' | 'commercial';

export enum LeadFormQuestionType {
  ShortText = 'ShortText',
  LongText = 'LongText',
  Dropdown = 'Dropdown',
  SingleChoice = 'SingleChoice',
  MultiChoice = 'MultiChoice',
  Date = 'Date',
  Number = 'Number',
  Email = 'Email',
  FileUpload = 'FileUpload',
  Address = 'Address',
  DataTable = 'DataTable',
}

/** Scalar column types for DataTable questions.
 *  Mirrors `ColumnScalarType` in phoenix/leadForm.ts (file_upload excluded). */
export type ColumnScalarType =
  | 'short_text'
  | 'long_text'
  | 'email'
  | 'number'
  | 'date'
  | 'dropdown'
  | 'single_choice'
  | 'multi_choice';

export interface ColumnValidation {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
}

export interface ColumnChoiceOption {
  label: string;
}

/** A single column definition in a DataTable question. */
export interface Column {
  id: string;
  label: string;
  type: ColumnScalarType;
  options?: ColumnChoiceOption[] | null;
  validation?: ColumnValidation | null;
}

/** One uploaded file for a FileUpload question; stored as an array under
 *  `lead_form_responses.responses[question_uuid]`. */
export interface UploadedFileRef {
  documentId: string;
  filename: string;
  contentType: string;
  size: number;
}

export enum FixedStepType {
  Welcome = 'Welcome',
  ContactInfo = 'ContactInfo',
  CarrierSelect = 'CarrierSelect',
  CarrierLogin = 'CarrierLogin',
  DataRetrieval = 'DataRetrieval',
  Confirmation = 'Confirmation',
  Complete = 'Complete',
}

export enum FormStatus {
  Draft = 'Draft',
  Active = 'Active',
  Archived = 'Archived',
}

export type StepSegment = 'before' | 'after';

export interface FixedStepConfig {
  headline?: string;
  description?: string;
  buttonText?: string;
  redirectUrl?: string;
  /** Override copy for the two feature bullets rendered on Welcome
   *  (`LandingViewTemplate.IconItem`). Hardcoded fallbacks live in
   *  Parrot's `WelcomeBody` ("Connect effortlessly" / "Your data is
   *  safe"). Welcome-only — Confirmation / Complete ignore them. */
  featureOneHeading?: string;
  featureOneDescription?: string;
  featureTwoHeading?: string;
  featureTwoDescription?: string;
}

export type RoutingType = 'continue' | 'show_question' | 'skip_to_step' | 'end_form';

/** Links a question's answer to a field on the client record so Parrot's
 *  submission handler (INS-541) can auto-populate Client fields from form
 *  responses. Mirrors Phoenix's `FieldMapping` enum. */
export enum FieldMapping {
  FullName = 'FullName',
  Email = 'Email',
  Phone = 'Phone',
  ServiceType = 'ServiceType',
  EventDate = 'EventDate',
}

export interface OptionRouting {
  type: RoutingType;
  targetQuestionId?: string;
  targetStepId?: string;
}

export interface QuestionOption {
  id: string;
  questionId: string;
  text: string;
  order: number;
  routing: OptionRouting;
}

export interface QuestionValidation {
  emailFormat?: boolean;
  urlFormat?: boolean;
  phoneFormat?: boolean;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: string;
  /** Open bag for type-specific extensions (e.g. FileUpload's `documentKind`). */
  [key: string]: unknown;
}

/**
 * Consumer-facing validation messages for lead-form question answers.
 *
 * Shared between Parrot's `FormView` (real consumer flow) and Eagle's
 * `PhonePreview` (agent-facing preview) so both surfaces show identical
 * copy. If you change a string here, both apps pick it up — no manual
 * sync, no drift. Add a new key + parameterized function as more
 * validation rules land (e.g. `pattern`, future `urlFormat`).
 */
export const LEAD_FORM_VALIDATION_MESSAGES = {
  required: 'This field is required.',
  invalidEmail: 'Enter a valid email address.',
  invalidPhone: 'Enter a valid US phone number.',
  invalidNumber: 'Enter a valid number.',
  minLength: (n: number) => `Must be at least ${n} characters.`,
  maxLength: (n: number) => `Must be at most ${n} characters.`,
  minValue: (n: number) => `Must be at least ${n}.`,
  maxValue: (n: number) => `Must be at most ${n}.`,
} as const;

// ─── INS-659: conditional logic rule builder ──────────────────────────────────

export type ConditionOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'notContains'
  | 'isEmpty'
  | 'isNotEmpty'
  | 'greaterThan'
  | 'lessThan'
  | 'before'
  | 'after';

export type RuleAction =
  | 'show'
  | 'hide'
  | 'require'
  | 'skip_to_step'
  | 'end_form'
  | 'continue';

export type ConditionMode = 'all' | 'any';

export interface LogicCondition {
  sourceQuestionId: string;
  operator: ConditionOperator;
  value?: string;
}

/** Top-level rule attached to a question. `id` is stable Eagle-generated. */
export interface QuestionRule {
  id: string;
  conditions: LogicCondition[];
  conditionMode: ConditionMode;
  action: RuleAction;
  targetQuestionId?: string;
  targetStepId?: string;
}

export interface FormQuestion {
  id: string;
  stepId: string;
  label: string;
  type: LeadFormQuestionType;
  order: number;
  required: boolean;
  placeholder?: string;
  options?: QuestionOption[];
  initiallyHidden?: boolean;
  fieldMapping?: FieldMapping;
  validation?: QuestionValidation;
  /** INS-659: top-level conditional logic rules on this question. The Eagle
   *  Logic modal merges these with virtual rules derived from option.routing
   *  to give the agent a single unified rule list. */
  rules?: QuestionRule[];
  /** DataTable column definitions. Only present when type=DataTable. */
  columnSchema?: Column[];
}

export interface FormStep {
  id: string;
  formId: string;
  label: string;
  order: number;
  isFixed: boolean;
  fixedType?: FixedStepType;
  segment?: StepSegment;
  config?: Partial<FixedStepConfig>;
  questions: FormQuestion[];
}

/** Per-form branding. `buttonColor` drives the consumer-facing CTA color
 *  (form.branding > agency.colorCode > legacy fallback). Button-text overrides
 *  live on each step's `FixedStepConfig.buttonText` — Welcome / Confirmation /
 *  Complete and custom steps each carry their own label so the form CTA can
 *  read "Get Started" on Welcome and "Continue" mid-flow.
 *
 *  `hideAgencyLogo` / `hideAgentHeadshot` / `hideTrustPill` / `hideDecorationRow`
 *  added 2026-05-20 (INS-698) for Priyank's embed-native ask — agents need a way
 *  to strip InsurGrid chrome when the form is embedded on their own site. */
export interface FormBranding {
  formId: string;
  buttonColor: string;
  logoUrl?: string;
  agentHeadshotUrl?: string;
  hideAgencyLogo?: boolean;
  hideAgentHeadshot?: boolean;
  hideTrustPill?: boolean;
  hideDecorationRow?: boolean;
}

// ─── INS-1314: custom intake form tracking ─────────────────────────────────

/** Per-form Meta Pixel / GTM configuration. Mirrors Phoenix's `FormTracking`
 *  Pydantic schema exactly (snake_case keys travel unchanged between the UI
 *  and the API — unlike the rest of `LeadForm`, this blob isn't reshaped on
 *  the way in or out). `enabled` gates whether Parrot loads the configured
 *  vendor(s) on a public visit; it requires at least one of
 *  `meta_pixel_id` / `gtm_container_id` / `custom_html` to be set. Phoenix
 *  rejects unknown keys and is the source of truth for validation — the
 *  helpers below exist purely for client-side save/publish UX. */
export interface FormTracking {
  enabled: boolean;
  meta_pixel_id: string | null;
  gtm_container_id: string | null;
  custom_html?: string | null;
}

const META_PIXEL_ID_PATTERN = /^\d{1,30}$/;
const GTM_CONTAINER_ID_PATTERN = /^GTM-[A-Z0-9]{4,20}$/;

function utf8ByteLength(value: string): number {
  let bytes = 0;
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 0x7f) bytes += 1;
    else if (codePoint <= 0x7ff) bytes += 2;
    else if (codePoint <= 0xffff) bytes += 3;
    else bytes += 4;
  }
  return bytes;
}

export function isValidMetaPixelId(value: string): boolean {
  return META_PIXEL_ID_PATTERN.test(value);
}

export function isValidGtmContainerId(value: string): boolean {
  return GTM_CONTAINER_ID_PATTERN.test(value);
}

/** Trims a raw form-field value and normalizes blanks to `null`. Mirrors
 *  Phoenix's backend normalization so a client-side round-trip matches what
 *  actually gets persisted. */
export function normalizeTrackingId(
  value: string | null | undefined,
): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/** Normalizes a (possibly agent-edited, possibly blank) tracking value into
 *  the wire shape: trimmed IDs, blanks as `null`, and the whole object
 *  collapsed to `null` when it carries no configuration at all (disabled,
 *  no IDs) — that's the "nothing configured" state and Phoenix clears the
 *  column when it receives `tracking: null`. An `enabled: true` value with
 *  no IDs is deliberately NOT collapsed here so `validateFormTracking` can
 *  still catch and report that invalid combination. */
export function normalizeFormTracking(
  tracking:
    | {
        enabled: boolean;
        meta_pixel_id?: string | null;
        gtm_container_id?: string | null;
        custom_html?: string | null;
      }
    | null
    | undefined,
): FormTracking | null {
  if (!tracking) return null;
  const normalized: FormTracking = {
    enabled: !!tracking.enabled,
    meta_pixel_id: normalizeTrackingId(tracking.meta_pixel_id),
    gtm_container_id: normalizeTrackingId(tracking.gtm_container_id),
    custom_html: normalizeTrackingId(tracking.custom_html),
  };
  const isEmpty =
    !normalized.enabled &&
    !normalized.meta_pixel_id &&
    !normalized.gtm_container_id &&
    !normalized.custom_html;
  return isEmpty ? null : normalized;
}

/** Client-side mirror of Phoenix's tracking validation, for save/publish UX.
 *  Phoenix remains authoritative — this only short-circuits obviously-bad
 *  input before a round trip. Returns a user-facing error string, or `null`
 *  when `tracking` is valid (including `null`, meaning "not configured"). */
export function validateFormTracking(
  tracking: FormTracking | null,
): string | null {
  if (!tracking) return null;
  const { enabled, meta_pixel_id, gtm_container_id, custom_html } = tracking;
  if (meta_pixel_id && !isValidMetaPixelId(meta_pixel_id)) {
    return 'Meta Pixel ID must be 1–30 digits.';
  }
  if (gtm_container_id && !isValidGtmContainerId(gtm_container_id)) {
    return 'GTM Container ID must start with GTM- followed by 4–20 uppercase letters/numbers.';
  }
  if (custom_html && utf8ByteLength(custom_html) > 10_000) {
    return 'Custom tracking HTML must be at most 10 KB.';
  }
  if (enabled && !meta_pixel_id && !gtm_container_id && !custom_html) {
    return 'Enabling tracking requires a tracking provider.';
  }
  return null;
}

export interface FormCreator {
  agentId: number | null;
  name: string | null;
  avatarUrl: string | null;
}

export interface LeadForm {
  id: string;
  name: string;
  shortCode: string;
  publicId: string;
  status: FormStatus;
  /** Agency that owns this form. Forms are scoped per-agency, not per-agent
   *  (see Priyank's 2026-04-08 override) — all agents in the agency can
   *  see/edit the same forms. Tracked here so UI guards can verify the API
   *  isn't returning forms from another tenant. */
  agencyId: string;
  steps: FormStep[];
  branding: FormBranding;
  carrierFlowEnabled: boolean;
  /** Line-of-business choice for the carrier handoff. Personal forms drive the
   *  consumer through the personal-lines AU sections (Drivers / Property /
   *  Agent Questions); commercial skips those and routes to the commercial
   *  carrier list. Ignored on `carrier_flow_enabled=false` form-only forms.
   *  Default 'personal' for backwards-compat with pre-INS-738 forms. */
  insuranceLine: InsuranceLine;
  /** True when the form's question label set exactly matches a TEMPLATE_SIGNATURES
   *  entry on the Phoenix side. Persisted forms get this from the API response
   *  (LeadFormRead.is_acord_ready); draft forms get it from the picked template at
   *  apply time so locks engage before the form is created. */
  isAcordReady: boolean;
  /** ACORD form short_ids (e.g. ['acord-125','acord-126']) when isAcordReady=true,
   *  null otherwise. Drives the amber 'Questions locked — generates X PDFs' callout. */
  acordForms: string[] | null;
  viewCount: number;
  submissionCount: number;
  completionRate: number;
  createdAt: string;
  updatedAt: string;
  creator?: FormCreator;
  /** INS-661: prospect auto-response email toggle. When enabled and the
   *  consumer provided an email (via FieldMapping.Email), Phoenix sends a
   *  Courier confirmation message on final submit. The email content lives
   *  in the Courier template; agents only flip the on/off switch. */
  autoResponseEnabled: boolean;
  /** INS-1314: optional Meta Pixel / GTM configuration. `null`/`undefined`
   *  means tracking has never been configured for this form. */
  tracking?: FormTracking | null;
}

export interface FormTemplate {
  id: string;
  name: string;
  icon: string;
  description: string;
  steps: Omit<FormStep, 'id' | 'formId'>[];
}
